import { api } from './client.js';

export interface SessionStatus {
  authenticated: boolean;
}

export interface HealthStatus {
  ok: boolean;
  storageConfigured: boolean;
  /** True while no password has been set — the client shows onboarding first. */
  firstRun: boolean;
}

export interface ChangePasswordResult {
  ok: true;
  /** How many other signed-in sessions were forced out. */
  othersSignedOut: number;
}

export const authApi = {
  session: () => api.get<SessionStatus>('/auth/session'),

  captcha: () => api.get<CaptchaChallenge>('/auth/captcha', { ignoreUnauthorized: true }),

  /** Note: a wrong password or captcha returns 401 and is expected, not a session loss. */
  login: (password: string, captchaId: string, captchaAnswer: string) =>
    api.post<{ ok: true }>(
      '/auth/login',
      { password, captchaId, captchaAnswer },
      { ignoreUnauthorized: true },
    ),

  logout: () => api.post<{ ok: true }>('/auth/logout'),

  /**
   * First-run password setup, before any sign-in.
   *
   * Only works while no password has been set yet; success issues a session
   * so the onboarding wizard lands straight in the library. A 409 means the
   * password was already set up elsewhere.
   */
  setupPassword: (newPassword: string) =>
    api.post<ChangePasswordResult>(
      '/auth/setup',
      { newPassword },
      { ignoreUnauthorized: true },
    ),

  /**
   * Changes the login password.
   *
   * A wrong current password returns 403 — a validation result, not a lost
   * session — so `ignoreUnauthorized` keeps the app from signing the user out.
   */
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<ChangePasswordResult>(
      '/auth/password',
      { currentPassword, newPassword },
      { ignoreUnauthorized: true },
    ),

  health: () => api.get<HealthStatus>('/health'),

  /** Auth audit trail for the settings UI (requires a session). */
  loginLog: () => api.get<LoginLogStatus>('/auth/login-log'),
};

export interface LoginRateLimitError {
  retryAfterSeconds: number;
}

export interface CaptchaChallenge {
  id: string;
  /** Rendered SVG markup for the captcha image. */
  svg: string;
}

export interface LoginLogEvent {
  /** Epoch ms (integer). */
  ts: number;
  action: 'login' | 'password' | 'setup';
  outcome: 'success' | 'failure' | 'blocked';
  reason: string;
  ip: string;
  /** Short parsed summary, e.g. "macOS · Chrome 131". */
  device: string;
  userAgent: string;
  failures: number | null;
  banned: boolean;
}

export interface LoginLogStatus {
  events: LoginLogEvent[];
  /** Epoch-ms deadline while the *viewing* IP is banned, else null. */
  blockedUntil: number | null;
  policy: { windowMinutes: number; maxFailures: number; blockHours: number };
}
